import { Heading, Link, Logo } from '@checkatrade/web-ui';
import { colors } from '@checkatrade/web-ui/themes';
import { Action, useAdServer } from '@hooks/use-ad-server';
import imageUrlBuilder from '@utils/image-url-builder';
import isBetweenHours from '@utils/is-between-hours';
import isWeekday from '@utils/is-weekday';
import getSchema from '@utils/map-category-to-schema';
import slugify from '@utils/slugify';
import { PaymentBadgeV2 } from 'components/@pages/CompanyPage/payment-badge-v2';
import { IconCheckMark, IconClock } from 'lib/design-system';
import type { SearchTrade, Trade } from 'lib/sdks/search';
import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { UAParser } from 'ua-parser-js';
import { TradeCardCoreSEO } from './TradeCardCoreSEO';
import {
  Availability,
  AvailabilityWrapper,
  Badge,
  ContactCtasWrapper,
  Description,
  FirstReviewFauxLink,
  InfoWrapper,
  Location,
  LocationAndAvailabilityWrapper,
  LocationPin,
  LogoAndInfoWrapper,
  MainContent,
  Rating,
  RatingScore,
  Reviews,
  SearchCardGrid,
  SkillItem,
  SkillItemWrapper,
} from './search-card.styles';
import { MostRecentlyReviewed } from './MostRecentlyReviewed';

export interface TempItem extends SearchTrade {
  skillsToPresent: { skills: string[]; totalSkills: number };
  payment?: { status?: string };
}

export interface SearchCardProps {
  companyInformation: SearchTrade;
  onMessageClick: (trade: Trade) => void;
  firstLoadedNewTradeId?: number;
  categoryId?: number;
  location?: string;
  source?: string;
  hideContactMessageCta?: boolean;
  showRecentlyReviewedBadge: boolean;
  operatingLocationText: string;
  onCardAppear: (
    entry: IntersectionObserverEntry,
    unobserve: () => void,
    data: {
      trade: SearchTrade;
      showRecentlyReviewedBadge: boolean;
    },
    order: number,
  ) => void;
  order: number;
  rootMargin: string;
  threshold: number;
  category: string;
}

export const SearchCard = ({
  companyInformation: _companyInformation,
  operatingLocationText,
  rootMargin,
  threshold,
  category,
  order,
  onCardAppear,
  firstLoadedNewTradeId,
  showRecentlyReviewedBadge,
  onMessageClick,
  categoryId,
  location,
  source,
  hideContactMessageCta = false,
}: SearchCardProps) => {
  const companyInformation = _companyInformation as TempItem;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const sponsoredBadge = companyInformation?.badges?.find(
    e => e.key === 'sponsored',
  );

  const { onClick: handleClickSponsored } = useAdServer({
    isSponsored: !!sponsoredBadge,
    isVisible,
    trackingId: sponsoredBadge?.trackingId,
    companyId: companyInformation?.companyId,
    searchType: 'Listings',
    placementType: 'SearchResult',
    campaignId: sponsoredBadge?.sponsoredCampaignId,
    rank: order,
  });

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: rootMargin,
      threshold: threshold,
    };

    const handleObserverChange = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        setIsVisible(true);
        onCardAppear(
          entry,
          () => {},
          {
            trade: companyInformation,
            showRecentlyReviewedBadge,
          },
          order,
        );
      }
    };

    const { current } = containerRef;
    const observer = new IntersectionObserver(handleObserverChange, options);
    if (current && !isVisible) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [
    showRecentlyReviewedBadge,
    containerRef,
    isVisible,
    rootMargin,
    threshold,
    companyInformation,
    order,
    onCardAppear,
  ]);

  const handleMessageClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onMessageClick(companyInformation as any);
    handleClickSponsored(Action.MESSAGE);
  };

  const handleTradeClick = (e: MouseEvent) => {
    e.stopPropagation();
    handleClickSponsored(Action.PROFILE);
  };

  const onClickCall = () => {
    handleClickSponsored(Action.CALL);
  };

  const onClickRevealPhone = () => {
    handleClickSponsored(Action.REVEAL_PHONE_NUMBER);
  };

  const searchCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isMobile: boolean = UAParser().device.type === 'mobile';
    const shouldScrollToTop =
      firstLoadedNewTradeId &&
      companyInformation.companyId === firstLoadedNewTradeId;

    if (isMobile && searchCardRef.current && shouldScrollToTop) {
      const offset = 132;

      const scrollToTop =
        searchCardRef.current.getBoundingClientRect().top +
        window.scrollY -
        offset;

      window.scrollTo({
        top: scrollToTop,
        behavior: 'smooth',
      });
    }
  }, [companyInformation, firstLoadedNewTradeId]);

  if (!companyInformation) {
    return null;
  }

  const {
    companyId,
    name,
    uniqueName,
    logoUrl,
    logoUrlTemplate,
    view: { showNewToCheckatrade: isNewMember, showMessage } = {
      showMessage: true,
    },
    address,
    reviewsSummary,
    summary,
    contact: { primary } = {},
    skillsToPresent,
    payment,
  } = companyInformation;

  const locality = address?.locality ?? undefined;

  const { recentMeanScore: rating, totalReviews } = reviewsSummary || {
    recentMeanScore: 0,
    totalReviews: 0,
  };

  const { number, label } = primary || {};

  const logoUrlFormatted = logoUrlTemplate
    ? imageUrlBuilder(logoUrlTemplate, {
        width: 100,
        format: 'webp',
        cacheLifetime: 365,
        compressionLevel: 1,
      })
    : '';
  const logoAlt = `${name} Logo`;

  const buildUrlFragment = () => {
    const params = new URLSearchParams();
    categoryId && params.append('categoryId', categoryId.toString());
    location && params.append('location', location);
    source && params.append('source', source);
    companyInformation.campaignId &&
      params.append('source_campaign', companyInformation.campaignId);
    return params.toString() ? `#${params.toString()}` : '';
  };

  const moreInfoAsUrl = `/trades/${uniqueName}`;
  const moreInfoAsUrlWithFragment = `/trades/${uniqueName}${buildUrlFragment()}`;
  const prioritiseCalls = isBetweenHours(8, 18) && isWeekday();
  const memberHasReviews = totalReviews > 0;

  return (
    // Convert to use `Card` component from web-ui when it supports 'ref'
    <div
      role="listitem"
      aria-label={name}
      data-guid="TradeCard"
      itemType={`http://schema.org/${getSchema(category.toLowerCase())}`}
      ref={searchCardRef}
      className="flex w-full flex-col overflow-hidden rounded-2xl border border-neutral-5 bg-card text-card-fg shadow-lg"
    >
      <TradeCardCoreSEO
        tradeProfileUrl={moreInfoAsUrl}
        operatingLocationText={operatingLocationText}
        logoUrl={logoUrl}
        telephoneNumber={number}
        locality={locality}
        addDescription={true}
        description={summary}
      />

      <MainContent
        className="relative"
        ref={containerRef}
        data-guid="TradeCardContent"
      >
        <SearchCardGrid
          showSponsoredBadge={!!sponsoredBadge}
          hasRecentlyReviewedBadge={showRecentlyReviewedBadge}
        >
          {sponsoredBadge && <Badge>{sponsoredBadge.label}</Badge>}
          <LogoAndInfoWrapper>
            <Logo
              src={logoUrlFormatted}
              alt={logoAlt}
              size="lg"
              lazy={order > 0}
            />
            <InfoWrapper>
              <Link
                className="after:absolute after:inset-0 hover:no-underline"
                href={moreInfoAsUrlWithFragment}
                onClick={handleTradeClick}
              >
                <Heading
                  data-guid="TradeCardName"
                  itemProp="name"
                  as="h4"
                  variant="headingMD"
                  className="text-2xl font-bold text-neutral-95"
                >
                  {name}
                </Heading>
              </Link>
              {memberHasReviews ? (
                <Rating data-guid="TradeCardRating">
                  {/* 0 rating should be temporarily hidden as atm it only happens when
                   * trader didn't receive any reviews in the last 12 months and this *
                   * information is not yet included in the search company information
                   * from the api */}
                  <RatingScore>{rating > 0 ? rating : ''}</RatingScore>
                  <Reviews>
                    ({`${totalReviews} review${totalReviews === 1 ? '' : 's'}`})
                  </Reviews>
                </Rating>
              ) : (
                <FirstReviewFauxLink>
                  {/* This is not actually a link because the card itself already links to the trade profile
                   * Nesting links kills SSR, but the UX decision is to keep this looking like a link */}
                  Be the first to review
                </FirstReviewFauxLink>
              )}

              <LocationAndAvailabilityWrapper>
                <Location data-guid="TradeCardLocation">
                  <LocationPin size="small" color={colors.secondary.DEFAULT} />
                  {operatingLocationText && (
                    <span>Operates in {operatingLocationText}</span>
                  )}
                </Location>

                {isNewMember && (
                  <AvailabilityWrapper>
                    <IconClock size="small" color={colors.secondary.DEFAULT} />
                    <Availability>Good availability</Availability>
                  </AvailabilityWrapper>
                )}

                <PaymentBadgeV2 payment={payment} location="search" />
              </LocationAndAvailabilityWrapper>
            </InfoWrapper>
          </LogoAndInfoWrapper>

          {showRecentlyReviewedBadge && (
            <div
              style={{ gridArea: 'recent-badge' }}
              className={'my-2 ml-4 md:my-0 md:ml-0'}
            >
              <MostRecentlyReviewed />
            </div>
          )}

          <Description>
            <Heading
              as="h5"
              variant="titleXS"
              className="font-semibold text-neutral-95"
            >
              <strong>Services & skills</strong>
            </Heading>
            <SkillItemWrapper>
              {skillsToPresent.skills.map(skill => (
                <SkillItem key={slugify(skill)}>
                  <IconCheckMark size="small" color={colors.secondary.alt} />
                  {skill}
                </SkillItem>
              ))}
              {skillsToPresent.totalSkills && (
                <span>&#43; {skillsToPresent.totalSkills} skills</span>
              )}
            </SkillItemWrapper>
          </Description>
          <ContactCtasWrapper
            className="z-10"
            company={{
              id: companyId,
              uniqueName,
              campaignId: companyInformation.campaignId,
            }}
            phone={{
              label,
              number,
            }}
            prioritiseCalls={prioritiseCalls}
            showMessageButton={hideContactMessageCta ? false : showMessage}
            handleMessageClick={handleMessageClick}
            onClickCall={onClickCall}
            onClickRevealPhone={onClickRevealPhone}
            inlineOnDesktop={false}
            sponsored={!!sponsoredBadge}
          />
        </SearchCardGrid>
      </MainContent>
    </div>
  );
};
